import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {Store} from '@ngrx/store';
import {Observable} from 'rxjs';
import {tap} from 'rxjs/operators';
import { AppConfig, APP_CONFIG_TOKEN } from "libs/config/app.lib.config";
import { SapService } from 'libs/sap/src/lib/services/sap.lib.service';
import { ProductKey } from 'libs/product/src/lib/models/product.model';
import { CancellationType, ChargeableRefinementPackage, FollowupPackageType, InterimScanOption, TreatmentModel } from 'libs/treatments/src/lib/models/treatment.model';
import { AppState } from 'libs/tm-common/src/lib/store/reducers/reducers';
import { WebViewerModificationDataContainer } from 'libs/web-viewer/src/lib/models/webviewer-data.model';
import type { MessageModel } from 'libs/messages/src/lib/models/message.model';

@Injectable({
	providedIn: 'root'
})
export class OrderService {
	private http = inject(HttpClient);
	private store = inject<Store<AppState>>(Store);
	private sapService = inject(SapService);
	private config = inject<AppConfig>(APP_CONFIG_TOKEN);

	anamnese;

	constructor() {

		this.store
			.pipe(
				tap(state => this.anamnese = state.anamnese),
			).subscribe();
	}

	getPriceForCancellation( productKey: ProductKey ) {
		return this.sapService.getPriceForCancellation(productKey);
	}

	getPriceForQuotation(treatmentId: string) {
		return this.sapService.getPriceForQuotation(treatmentId);
	}

	cancelOrder(treatmentId: string, cancellationComment: string = "") {
		const comment = `***Stornierungsgrund***: ${cancellationComment}`;

		const requestBody = {
			comment: comment,
			type: CancellationType.CUSTOMER
		};
		return this.http
			.post(`${this.config.apiUrl}/treatments/${treatmentId}/cancelTreatment`, requestBody,
				{withCredentials: true});
	}

	cancelTreatmentFreeOfCharge(treatmentId: string): Observable<object>{
		const comment = `Treatment wurde kostenfrei storniert`;
		const requestBody = {
			comment: comment,
			type: CancellationType.GOODWILL
		};
		return this.http
			.post(`${this.config.apiUrl}/treatments/${treatmentId}/cancelTreatment`, requestBody,
				{withCredentials: true});
	}

	requestSetupCorrection(
		treatmentId: string,
		comment: string,
		webviewerUrl?: string,
		webviewerModification?: WebViewerModificationDataContainer,
		language?: "de" | "en"
	): Observable<{ treatment: TreatmentModel, message?: MessageModel }> {
		// See RequestSetupCorrectionDto in tm-server
		const requestBody = {
			comment: comment,
			webviewerUrl: webviewerUrl,
			webviewerModification: webviewerModification,
			language: language
		};
		return this.http.post<{ treatment: TreatmentModel, message?: MessageModel }>(
			`${this.config.apiUrl}/treatments/${treatmentId}/requestSetupCorrection`,
			requestBody,
			{ withCredentials: true }
		);
	}

	alignerReleaseOrder(treatmentId: string, comment: string, isAlternativeOption: boolean = false): Observable<TreatmentModel> {
		const payload = {
			comment: comment,
			isUpgrade: isAlternativeOption
		}
		return <Observable<TreatmentModel>>this.http
			.post(`${this.config.apiUrl}/treatments/${treatmentId}/quotation/release`, payload, { withCredentials: true });
	}

	alignerFollowupOrder(treatmentId: string, packageType: FollowupPackageType, interimScanOption: InterimScanOption): Observable<TreatmentModel> {
		const payload = {
			packageType: packageType,
			interimScanOption: interimScanOption,
		}
		return <Observable<TreatmentModel>>this.http
			.post(`${this.config.apiUrl}/treatments/${treatmentId}/packages/next`, payload, { withCredentials: true });
	}

	requestRefinement(treatmentId: string, comment?: string): Observable<TreatmentModel> {
		const payload = {
				comment: comment ?? null
		}
		return <Observable<TreatmentModel>>this.http
			.post(`${this.config.apiUrl}/treatments/${treatmentId}/refinement/request`, payload, { withCredentials: true });
	}

	requestRefinementRelease(treatmentId: string, plannedAligners: ChargeableRefinementPackage | number ): Observable<TreatmentModel> {
		const payload = {
			plannedAligners: plannedAligners
		}
		return <Observable<TreatmentModel>>this.http
			.post(`${this.config.apiUrl}/treatments/${treatmentId}/refinement/releaseRequest`, payload, { withCredentials: true });
	}

	releaseRefinementRequest(treatmentId: string): Observable<TreatmentModel> {
		return <Observable<TreatmentModel>>this.http
			.post(`${this.config.apiUrl}/treatments/${treatmentId}/refinement/release`, null, { withCredentials: true });
	}

	rejectRefinementRequest(treatmentId: string): Observable<TreatmentModel> {
		return <Observable<TreatmentModel>>this.http
			.post(`${this.config.apiUrl}/treatments/${treatmentId}/refinement/reject`, null, { withCredentials: true });
	}
}
